import React, { useState, useEffect, useContext } from "react";
import "../../App.css";
import { FaSearch, FaPlus } from "react-icons/fa";
import AddPoliticians from "./AddPoliticians";
import { UserContext } from "../context/UserContext";

const PoliticiansList = ({ onPoliticianClick }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddPoliticiansOpen, setIsAddPoliticiansOpen] = useState(false);
  const [politicians, setPoliticians] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { emailId } = useContext(UserContext);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [editPolitician, setEditPolitician] = useState(null);

  // Check if user is admin
  const isAdmin = emailId === "info@urcommunity.com.au" || emailId === "info@urcommunity.org.au";

  // Screen size detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePoliticianSelection = (politician) => {
    try {
      localStorage.setItem("selectedPoliticianId", politician.id);
      localStorage.setItem("selectionType", "politician");
      // Dispatch custom event for immediate state update
      window.dispatchEvent(new CustomEvent('politicianSelected', {
        detail: { politicianId: politician.id }
      }));
      if (onPoliticianClick) {
        onPoliticianClick("politician");
      }
    } catch (error) {
      console.error("Error selecting politician:", error);
    }
  };

  // Fetch politicians data
  useEffect(() => {
    const fetchPoliticians = async () => {
      try {
        const response = await fetch("/api/politicians");
        if (response.ok) {
          const data = await response.json();
          setPoliticians(data);
        } else {
          console.error("Failed to fetch politicians");
        }
      } catch (error) {
        console.error("Error fetching politicians:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPoliticians();
    const intervalId = setInterval(fetchPoliticians, 5000);
    return () => clearInterval(intervalId);
  }, []);

  // Filter politicians based on admin status and search
  const filteredPoliticians = politicians
    .filter((politician) => {
      // Admin sees all, normal users only see those with is_displayed === 1
      if (isAdmin) return true;
      return politician.is_displayed === 1;
    })
    .filter((politician) => {
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      return (
        politician.name?.toLowerCase().includes(searchLower) ||
        politician.party?.toLowerCase().includes(searchLower) ||
        politician.constituency?.toLowerCase().includes(searchLower) ||
        politician.position?.toLowerCase().includes(searchLower)
      );
    });

  return (
    <div className="politicians-list-container" style={{ 
      padding: "10px",
      height: "100%",
      overflow: "hidden"
    }}>
      {/* Search bar and Add button */}
      <div
        className="search-add-container"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px",
          backgroundColor: "#fff",
          zIndex: 1,
          padding: "10px 0"
        }}
      >
        {/* Search Bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            position: "relative",
            width: "300px",
            height: "40px",
            background: "linear-gradient(to right, #007bff, #00d4ff)",
            borderRadius: "50px",
            padding: "10px",
          }}
        >
          <FaSearch
            style={{
              fontSize: "18px",
              color: "white",
              marginRight: "10px",
            }}
          />
          <input
            type="text"
            placeholder="Search politicians..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              border: "none",
              outline: "none",
              backgroundColor: "transparent",
              color: "#fff",
              fontSize: "14px",
              caretColor: "#fff",
            }}
            className="search-input-placeholder"
          />
        </div>

        {/* Add Button - Only show for admin */}
        {isAdmin && (
          <button
            onClick={() => setIsAddPoliticiansOpen(true)}
            style={{
              background: "linear-gradient(to right, #007bff, #00d4ff)",
              color: "#fff",
              border: "none",
              padding: "10px",
              borderRadius: "50%",
              fontSize: "18px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "40px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            <FaPlus />
          </button>
        )}
      </div>

      {/* Politicians List - Scrollable */}
      <div
        className="politicians-list"
        style={{
          display: "grid",
          gap: "20px",
          maxHeight: "calc(100vh - 180px)",
          overflowY: "auto",
          padding: "10px 10px 100px 0",
          marginRight: "5px"
        }}
      >
        {isLoading ? (
          <p>Loading politicians...</p>
        ) : (
          filteredPoliticians.map((politician) => (
            <div
              key={politician.id}
              onClick={() => handlePoliticianSelection(politician)}
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: "15px",
                padding: "10px",
                backgroundColor: "#fff",
                borderRadius: "10px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                alignItems: isMobile ? "center" : "flex-start",
                width: isMobile ? "100%" : "380px",
                cursor: "pointer",
                position: "relative"
              }}
            >
              {/* Thumbnail */}
              <div
                className="politician-thumbnail"
                style={{
                  width: isMobile ? "100%" : "150px",
                  height: isMobile ? "auto" : "100px",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                {politician.image_url ? (
                  <img
                    src={politician.image_url}
                    alt={politician.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "#e0e0e0",
                      color: "#555",
                      fontSize: "12px",
                    }}
                  >
                    No image available
                  </div>
                )}
              </div>

              {/* Politician Info */}
              <div
                className="politician-info"
                style={{
                  flex: "1",
                  marginTop: isMobile ? "10px" : "0",
                  textAlign: isMobile ? "center" : "left",
                }}
              >
                <h5
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginBottom: "4px",
                  }}
                >
                  {politician.name}
                </h5>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#555",
                    margin: "0 0 8px",
                    fontWeight: "bold",
                  }}
                >
                  {politician.party}
                </p>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#555",
                    margin: "0 0 8px",
                  }}
                >
                  {politician.constituency}
                </p>
                <div style={{ marginTop: "5px" }}>
                  <div
                    style={{
                      height: "10px",
                      background: "#e0e0e0",
                      borderRadius: "5px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${Math.min(
                          ((politician.total_raised || 0) /
                            (politician.target_amount || 1)) *
                            100,
                          100
                        )}%`,
                        background: "linear-gradient(to right, #007bff, #00d4ff)",
                        borderRadius: "5px",
                        transition: "width 0.3s ease-in-out",
                      }}
                    ></div>
                  </div>
                  <p
                    style={{
                      marginTop: "5px",
                      fontSize: "12px",
                      color: "#777",
                    }}
                  >
                    ${parseFloat(politician.total_raised || 0).toFixed(2)} raised of $
                    {parseFloat(politician.target_amount || 0).toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Edit Button for Admin */}
              {isAdmin && (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    setEditPolitician(politician);
                    setIsAddPoliticiansOpen(true);
                  }}
                  style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    background: "#ffc107",
                    color: "#333",
                    border: "none",
                    borderRadius: "4px",
                    padding: "4px 10px",
                    fontSize: "12px",
                    cursor: "pointer",
                    zIndex: 2
                  }}
                  title="Edit Politician"
                >
                  Edit
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {/* AddPoliticians Popup */}
      {isAddPoliticiansOpen && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            zIndex: 1000,
          }}
        >
          <AddPoliticians 
            onClose={() => {
              setIsAddPoliticiansOpen(false);
              setEditPolitician(null);
            }}
            politician={editPolitician}
          />
        </div>
      )}
    </div>
  );
};

export default PoliticiansList;
